import { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useVideoAds, adminApi } from '@hooks/useAdminApi';
import { toastManager } from './ToastContainer';
import { api } from '@utils/api';

function AdsManager() {
    const [mobileBanner, setMobileBanner] = useState({ imageUrl: '', targetUrl: '' });
    const [desktopBanner, setDesktopBanner] = useState({ imageUrl: '', targetUrl: '' });
    const [isSaving, setIsSaving] = useState(false);
    const [showAddVideoModal, setShowAddVideoModal] = useState(false);
    const [newVideoAd, setNewVideoAd] = useState({
        title: '',
        videoUrl: '',
        clickUrl: '',
        platform: 'all'
    });
    const [isUploading, setIsUploading] = useState(false);
    
    const { ads, isLoading, mutate } = useVideoAds();
    
    useEffect(() => {
        // Load banners on mount
        const loadBanners = async () => {
            try {
                const result = await adminApi.getBanners();
                if (result.success && result.banners) {
                    setMobileBanner(result.banners.mobile || { imageUrl: '', targetUrl: '' });
                    setDesktopBanner(result.banners.desktop || { imageUrl: '', targetUrl: '' });
                }
            } catch (error) {
                console.error('Failed to load banners:', error);
            }
        };
        
        loadBanners();
    }, []); // ✅ Empty dependency - only load once!
    
    const handleSaveBanner = useCallback(async (type) => {
        const banner = type === 'mobile' ? mobileBanner : desktopBanner;
        
        if (!banner.imageUrl) {
            toastManager.error('Error', 'Please enter banner image URL');
            return;
        }
        
        if (isSaving) return;
        
        try {
            setIsSaving(true);
            const result = await adminApi.updateBanners({
                [type]: banner
            });
            
            if (result.success) {
                toastManager.success('Success', `${type} banner updated successfully`);
            } else {
                throw new Error(result.message);
            }
        } catch (error) {
            toastManager.error('Error', error.message || 'Failed to update banner');
        } finally {
            setIsSaving(false);
        }
    }, [mobileBanner, desktopBanner, isSaving]);
    
    const handleToggleAd = useCallback(async (adId, isActive) => {
        try {
            const result = await adminApi.toggleAdActive(adId, isActive);
            
            if (result.success) {
                mutate(); // Refresh ads list
                toastManager.success('Success', `Ad ${isActive ? 'activated' : 'deactivated'} successfully`);
            } else {
                throw new Error(result.message || 'Failed to toggle ad');
            }
        } catch (error) {
            console.error('Toggle ad error:', error);
            toastManager.error('Error', error.message || 'Failed to toggle ad status');
            mutate(); // Refresh to revert the UI state
        }
    }, [mutate]);
    
    const handleDeleteAd = useCallback(async (adId) => {
        if (!confirm('Are you sure you want to delete this ad?')) return;
        
        try {
            await adminApi.deleteAd(adId);
            mutate();
            toastManager.success('Success', 'Ad deleted successfully');
        } catch (error) {
            toastManager.error('Error', 'Failed to delete ad');
        }
    }, [mutate]);

    const handleAddVideoAd = useCallback(async () => {
        if (!newVideoAd.title || !newVideoAd.videoUrl) {
            toastManager.error('Error', 'Title and Video URL are required');
            return;
        }

        try {
            setIsUploading(true);
            const result = await adminApi.createAd({
                title: newVideoAd.title,
                file_url: newVideoAd.videoUrl,
                click_url: newVideoAd.clickUrl || '',
                platform: newVideoAd.platform,
                is_active: true
            });

            if (result.success) {
                toastManager.success('Success', 'Video ad added successfully');
                setShowAddVideoModal(false);
                setNewVideoAd({ title: '', videoUrl: '', clickUrl: '', platform: 'all' });
                mutate();
            } else {
                throw new Error(result.message);
            }
        } catch (error) {
            toastManager.error('Error', error.message || 'Failed to add video ad');
        } finally {
            setIsUploading(false);
        }
    }, [newVideoAd, mutate]);
    
    return (
        <div className="admin-section">
            <h2 className="admin-section-title">
                <i className="fas fa-ad"></i> Ads Manager
            </h2>
            
            {/* Banner Ads */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '25px', marginBottom: '40px' }}>
                {/* Mobile Banner */}
                <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '16px', padding: '25px' }}>
                    <h3 style={{ color: '#d4af37', marginBottom: '20px' }}>
                        <i className="fas fa-mobile-alt"></i> Mobile Banner
                    </h3>
                    <div className="admin-form-group">
                        <label>Image URL</label>
                        <input
                            type="url"
                            className="admin-input"
                            value={mobileBanner.imageUrl}
                            onChange={(e) => setMobileBanner({ ...mobileBanner, imageUrl: e.target.value })}
                            placeholder="https://example.com/banner.gif"
                        />
                    </div>
                    <div className="admin-form-group">
                        <label>Target URL</label>
                        <input
                            type="url"
                            className="admin-input"
                            value={mobileBanner.targetUrl}
                            onChange={(e) => setMobileBanner({ ...mobileBanner, targetUrl: e.target.value })}
                            placeholder="https://example.com"
                        />
                    </div>
                    <button className="admin-btn" onClick={() => handleSaveBanner('mobile')}>
                        Update Mobile Banner
                    </button>
                    {mobileBanner.imageUrl && (
                        <div style={{ marginTop: '20px', border: '2px dashed rgba(255,215,0,0.2)', borderRadius: '12px', padding: '15px', textAlign: 'center' }}>
                            <img 
                                src={mobileBanner.imageUrl} 
                                alt="Mobile Banner Preview" 
                                loading="lazy"
                                style={{ maxWidth: '100%', borderRadius: '8px' }} 
                            />
                        </div>
                    )}
                </div>
                
                {/* Desktop Banner */}
                <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '16px', padding: '25px' }}>
                    <h3 style={{ color: '#d4af37', marginBottom: '20px' }}>
                        <i className="fas fa-desktop"></i> Desktop Banner
                    </h3>
                    <div className="admin-form-group">
                        <label>Image URL</label>
                        <input
                            type="url"
                            className="admin-input"
                            value={desktopBanner.imageUrl}
                            onChange={(e) => setDesktopBanner({ ...desktopBanner, imageUrl: e.target.value })}
                            placeholder="https://example.com/banner.gif"
                        />
                    </div>
                    <div className="admin-form-group">
                        <label>Target URL</label>
                        <input
                            type="url"
                            className="admin-input"
                            value={desktopBanner.targetUrl}
                            onChange={(e) => setDesktopBanner({ ...desktopBanner, targetUrl: e.target.value })}
                            placeholder="https://example.com"
                        />
                    </div>
                    <button className="admin-btn" onClick={() => handleSaveBanner('desktop')}>
                        Update Desktop Banner
                    </button>
                    {desktopBanner.imageUrl && (
                        <div style={{ marginTop: '20px', border: '2px dashed rgba(255,215,0,0.2)', borderRadius: '12px', padding: '15px', textAlign: 'center' }}>
                            <img 
                                src={desktopBanner.imageUrl} 
                                alt="Desktop Banner Preview" 
                                loading="lazy"
                                style={{ maxWidth: '100%', borderRadius: '8px' }} 
                            />
                        </div>
                    )}
                </div>
            </div>
            
            {/* Video Ads */}
            <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '16px', padding: '30px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '1rem' }}>
                    <h3 style={{ color: '#d4af37', margin: 0 }}>
                        <i className="fas fa-video"></i> Video Ads (In-Player)
                    </h3>
                    <div style={{ display: 'flex', gap: '1rem', position: 'relative', zIndex: 20 }}>
                        <button 
                            className="admin-btn admin-btn-secondary" 
                            onClick={() => {
                                console.log('[AdsManager] Refresh ads clicked');
                                mutate();
                            }}
                            title="Refresh ads list"
                            style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                        >
                            <i className="fas fa-sync-alt"></i> Refresh
                        </button>
                        <button 
                            className="admin-btn" 
                            onClick={() => {
                                console.log('[AdsManager] Add Video Ad button clicked');
                                setShowAddVideoModal(true);
                            }}
                            style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                        >
                            <i className="fas fa-plus"></i> Add Video Ad
                        </button>
                    </div>
                </div>
                
                {isLoading ? (
                    <div className="admin-loading"><i className="fas fa-spinner fa-spin"></i><p>Loading ads...</p></div>
                ) : ads.length === 0 ? (
                    <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)', padding: '40px' }}>No video ads found</p>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Title</th>
                                    <th>Platform</th>
                                    <th>Active</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {ads.map(ad => (
                                    <tr key={ad.id}>
                                        <td>{ad.title || 'Untitled'}</td>
                                        <td><span className="status-badge">{ad.platform}</span></td>
                                        <td>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={ad.is_active}
                                                    onChange={(e) => handleToggleAd(ad.id, e.target.checked)}
                                                />
                                                <span style={{ color: ad.is_active ? '#00d084' : '#718096' }}>
                                                    {ad.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </label>
                                        </td>
                                        <td>
                                            <button 
                                                className="admin-btn admin-btn-sm admin-btn-danger"
                                                onClick={() => handleDeleteAd(ad.id)}
                                            >
                                                <i className="fas fa-trash"></i> Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Add Video Ad Modal - Portal to body */}
            {showAddVideoModal && createPortal(
                <div 
                    onClick={() => setShowAddVideoModal(false)}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0, 0, 0, 0.98)',
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                        zIndex: 2147483647, /* Max z-index */
                        padding: '1rem',
                        paddingTop: '100px',
                        overflowY: 'auto'
                    }}
                >
                    <div 
                        onClick={(e) => e.stopPropagation()} 
                        style={{ 
                            maxWidth: '600px',
                            width: '90%',
                            background: 'linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%)',
                            border: '2px solid rgba(255, 215, 0, 0.3)',
                            borderRadius: '20px',
                            maxHeight: '90vh',
                            overflowY: 'auto',
                            boxShadow: '0 20px 60px rgba(0,0,0,0.8), 0 0 80px rgba(255,215,0,0.1)'
                        }}
                    >
                        <div style={{
                            padding: '1.5rem',
                            borderBottom: '1px solid rgba(255,215,0,0.2)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <h3 style={{
                                margin: 0,
                                background: 'linear-gradient(135deg, #FFD700, #FFC107)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                fontSize: '1.5rem',
                                fontWeight: 800
                            }}><i className="fas fa-plus-circle"></i> Add New Video Ad</h3>
                            <button 
                                onClick={() => setShowAddVideoModal(false)}
                                style={{
                                    background: 'rgba(255,255,255,0.1)',
                                    border: 'none',
                                    color: '#fff',
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '10px',
                                    cursor: 'pointer',
                                    fontSize: '1.2rem'
                                }}
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        
                        <div style={{ padding: '1.5rem' }}>
                            <div style={{ marginBottom: '1.25rem' }}>
                                <label style={{ display: 'block', color: '#FFD700', marginBottom: '0.5rem', fontWeight: 600 }}>
                                    <i className="fas fa-heading"></i> Ad Title *
                                </label>
                                <input
                                    type="text"
                                    value={newVideoAd.title}
                                    onChange={(e) => setNewVideoAd({ ...newVideoAd, title: e.target.value })}
                                    placeholder="e.g., Summer Sale Ad"
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem 1rem',
                                        background: 'rgba(0,0,0,0.4)',
                                        border: '2px solid rgba(255,215,0,0.2)',
                                        borderRadius: '10px',
                                        color: '#fff',
                                        fontSize: '1rem'
                                    }}
                                />
                            </div>

                            <div style={{ marginBottom: '1.25rem' }}>
                                <label style={{ display: 'block', color: '#FFD700', marginBottom: '0.5rem', fontWeight: 600 }}>
                                    <i className="fas fa-video"></i> Video URL *
                                </label>
                                <input
                                    type="url"
                                    value={newVideoAd.videoUrl}
                                    onChange={(e) => setNewVideoAd({ ...newVideoAd, videoUrl: e.target.value })}
                                    placeholder="https://example.com/video.mp4"
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem 1rem',
                                        background: 'rgba(0,0,0,0.4)',
                                        border: '2px solid rgba(255,215,0,0.2)',
                                        borderRadius: '10px',
                                        color: '#fff',
                                        fontSize: '1rem'
                                    }}
                                />
                                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                                    <i className="fas fa-info-circle"></i> Direct link to MP4 video file
                                </p>
                            </div>

                            <div style={{ marginBottom: '1.25rem' }}>
                                <label style={{ display: 'block', color: 'rgba(255,255,255,0.8)', marginBottom: '0.5rem', fontWeight: 600 }}>
                                    <i className="fas fa-external-link-alt"></i> Click URL (Optional)
                                </label>
                                <input
                                    type="url"
                                    value={newVideoAd.clickUrl}
                                    onChange={(e) => setNewVideoAd({ ...newVideoAd, clickUrl: e.target.value })}
                                    placeholder="https://example.com/landing-page"
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem 1rem',
                                        background: 'rgba(0,0,0,0.4)',
                                        border: '2px solid rgba(255,215,0,0.2)',
                                        borderRadius: '10px',
                                        color: '#fff',
                                        fontSize: '1rem'
                                    }}
                                />
                                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                                    <i className="fas fa-info-circle"></i> Where to redirect when ad is clicked
                                </p>
                            </div>

                            <div style={{ marginBottom: '1.25rem' }}>
                                <label style={{ display: 'block', color: 'rgba(255,255,255,0.8)', marginBottom: '0.5rem', fontWeight: 600 }}>
                                    <i className="fas fa-mobile-alt"></i> Platform
                                </label>
                                <select
                                    value={newVideoAd.platform}
                                    onChange={(e) => setNewVideoAd({ ...newVideoAd, platform: e.target.value })}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem 1rem',
                                        background: 'rgba(0,0,0,0.4)',
                                        border: '2px solid rgba(255,215,0,0.2)',
                                        borderRadius: '10px',
                                        color: '#fff',
                                        fontSize: '1rem'
                                    }}
                                >
                                    <option value="all">Both (Mobile & Desktop)</option>
                                    <option value="mobile">Mobile Only</option>
                                    <option value="desktop">Desktop Only</option>
                                </select>
                            </div>

                            {/* Preview */}
                            {newVideoAd.videoUrl && (
                                <div style={{ marginBottom: '1.5rem', padding: '1rem', background: 'rgba(255,215,0,0.05)', borderRadius: '12px', border: '1px solid rgba(255,215,0,0.1)' }}>
                                    <p style={{ color: '#FFD700', marginBottom: '10px', fontWeight: 'bold' }}>
                                        <i className="fas fa-eye"></i> Preview:
                                    </p>
                                    <video
                                        src={newVideoAd.videoUrl}
                                        controls
                                        style={{ width: '100%', borderRadius: '8px', maxHeight: '250px' }}
                                        onError={() => {
                                            toastManager.error('Error', 'Failed to load video preview');
                                        }}
                                    />
                                </div>
                            )}
                        </div>

                        <div style={{ 
                            display: 'flex', 
                            gap: '1rem', 
                            padding: '1.5rem',
                            borderTop: '1px solid rgba(255,215,0,0.15)'
                        }}>
                            <button 
                                onClick={() => setShowAddVideoModal(false)}
                                disabled={isUploading}
                                style={{
                                    flex: 1,
                                    padding: '0.875rem 1.5rem',
                                    background: 'rgba(0,0,0,0.4)',
                                    border: '1px solid rgba(255,215,0,0.2)',
                                    borderRadius: '10px',
                                    color: 'rgba(255,255,255,0.8)',
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    cursor: 'pointer'
                                }}
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={handleAddVideoAd}
                                disabled={isUploading || !newVideoAd.title || !newVideoAd.videoUrl}
                                style={{
                                    flex: 1,
                                    padding: '0.875rem 1.5rem',
                                    background: isUploading || !newVideoAd.title || !newVideoAd.videoUrl 
                                        ? 'rgba(255,215,0,0.3)' 
                                        : 'linear-gradient(135deg, #FFD700 0%, #FFC107 50%, #B8860B 100%)',
                                    border: 'none',
                                    borderRadius: '10px',
                                    color: '#000',
                                    fontSize: '1rem',
                                    fontWeight: 700,
                                    cursor: isUploading || !newVideoAd.title || !newVideoAd.videoUrl ? 'not-allowed' : 'pointer',
                                    boxShadow: '0 4px 15px rgba(255,215,0,0.3)',
                                    opacity: isUploading || !newVideoAd.title || !newVideoAd.videoUrl ? 0.5 : 1
                                }}
                            >
                                {isUploading ? (
                                    <><i className="fas fa-spinner fa-spin"></i> Adding...</>
                                ) : (
                                    <><i className="fas fa-plus"></i> Add Video Ad</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}

export default AdsManager;

